import React, { useEffect, useRef, useState } from 'react';
import './JneuralTerminal.css';

/**
 * jneural Terminal — public-facing slice of terminal.jneural.net
 *
 * The platform does a lot more than spot crypto prices, so this section
 * shows the spread of what the stack actually runs:
 *   • Live market quotes (real, CoinGecko)
 *   • Infra telemetry (mocked but plausibly numbered + animated)
 *   • Inference queue (Jetson GPU + on-host CUDA queue)
 *   • Container fleet status
 *   • Sentiment / news ticker (rotating)
 *
 * Everything talks to the same self-hosted stack documented in the CV
 * (Caddy + Cloudflared + Docker, jneural.net behind the home network).
 */

const SENTIMENT = [
  '⊙ BTC orderbook depth ▲ 4.2% (5m)',
  '⊙ inference job: yolov8n-int8 · 17 in flight',
  '⊙ GitLab pipeline #482 · PASS (4m18s)',
  '⊙ MAVLink replay buffer · 2.1GB · 84% retained',
  '⊙ TGT classifier retrained · F1 0.917 ↑',
  '⊙ Caddy → terminal.jneural · 5xx rate 0.00%',
  '⊙ tunnel cloudflared · uptime 21d 06h',
  '⊙ inference-worker · GPU 73°C · 14 containers up',
];

function useTicker(ms = 2400) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % SENTIMENT.length), ms);
    return () => clearInterval(id);
  }, [ms]);
  return SENTIMENT[i];
}

function useMarket() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancel = false;
    const fetchData = async () => {
      try {
        const r = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana&vs_currencies=gbp&include_24hr_change=true'
        );
        if (!r.ok) throw new Error('http');
        const j = await r.json();
        if (!cancel) { setData(j); setErr(false); }
      } catch (e) {
        if (!cancel) setErr(true);
      }
    };
    fetchData();
    const id = setInterval(fetchData, 30000);
    return () => { cancel = true; clearInterval(id); };
  }, []);
  return { data, err };
}

/** rAF-driven telemetry numbers — they wiggle like real metrics. */
function useTelemetry() {
  const [t, setT] = useState({
    gpuTemp: 71, cpuLoad: 38, ram: 62, jobs: 17, containers: 14,
    netRx: 412, netTx: 188, uptimeH: 506,
  });
  const ref = useRef(t);
  ref.current = t;
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const tick = (now) => {
      if (now - last > 1100) {
        last = now;
        const cur = ref.current;
        const drift = (base, span, min, max) =>
          Math.max(min, Math.min(max, base + (Math.random() - 0.5) * span));
        setT({
          gpuTemp: drift(cur.gpuTemp, 1.2, 64, 82),
          cpuLoad: drift(cur.cpuLoad, 4.5, 18, 78),
          ram: drift(cur.ram, 1.8, 42, 84),
          jobs: Math.max(0, Math.min(40, Math.round(drift(cur.jobs, 1.6, 4, 32)))),
          containers: 14,
          netRx: drift(cur.netRx, 60, 80, 980),
          netTx: drift(cur.netTx, 40, 40, 620),
          uptimeH: cur.uptimeH + 1 / 3600,
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return t;
}

function MarketRow({ k, name, ticker, data, err }) {
  const row = data?.[k];
  const price = row?.gbp;
  const change = row?.gbp_24h_change;
  const up = (change ?? 0) >= 0;
  return (
    <div className="jt-asset">
      <div className="jt-asset__id">
        <span className="jt-asset__ticker">{ticker}</span>
        <span className="jt-asset__name">{name}</span>
      </div>
      <div className="jt-asset__price">
        {err ? <span className="jt-err">link · degraded</span>
             : price == null ? <span className="jt-dim">— —</span>
             : `£${price.toLocaleString(undefined, { maximumFractionDigits: price > 100 ? 0 : 2 })}`}
      </div>
      <div className={`jt-asset__chg ${up ? 'up' : 'down'}`}>
        {change == null ? '—' : `${up ? '▲' : '▼'} ${Math.abs(change).toFixed(2)}%`}
      </div>
    </div>
  );
}

function Spark({ value, max, accent = '#4ad99c' }) {
  // Inline mini bar — value is current, accent is high-conf colour
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="jt-spark">
      <div className="jt-spark__fill" style={{ width: `${pct}%`, background: accent }} />
    </div>
  );
}

const JneuralTerminal = () => {
  const ticker = useTicker();
  const { data, err } = useMarket();
  const tel = useTelemetry();

  return (
    <section
      id="terminal"
      className="jt-section"
      data-section-theme="terminal"
      style={{
        '--nav-bg': 'rgba(8,12,20,0.7)',
        '--nav-text': '#f5f2ec',
        '--nav-accent': '#4ad99c',
        '--nav-border': 'rgba(245,242,236,0.08)',
        '--section-bg': '#080c14',
      }}
    >
      <div className="jt-inner">
        <header className="jt-header">
          <div>
            <span className="jt-eyebrow">terminal.jneural.net · public read</span>
            <h2 className="jt-title">jneural Terminal</h2>
            <p className="jt-sub">
              A thin window onto the self-hosted stack — market data, infrastructure
              telemetry, model inference queue and pipeline health. The platform does
              more than crypto prices; this is a sample of what it actually serves.
            </p>
          </div>
          <div className="jt-ticker" aria-live="polite">
            <span className="jt-ticker__dot" />
            <span className="jt-ticker__msg">{ticker}</span>
          </div>
        </header>

        <div className="jt-grid">
          {/* Market */}
          <div className="jt-panel jt-panel--market">
            <div className="jt-panel__hdr">
              <span>MARKET · GBP</span>
              <span className="jt-dim">CoinGecko · 30s</span>
            </div>
            <div className="jt-asset-list">
              <MarketRow k="bitcoin"  name="Bitcoin"  ticker="BTC" data={data} err={err} />
              <MarketRow k="ethereum" name="Ethereum" ticker="ETH" data={data} err={err} />
              <MarketRow k="solana"   name="Solana"   ticker="SOL" data={data} err={err} />
            </div>
            <div className="jt-panel__foot">
              quotes drive an internal portfolio engine that rebalances on a fixed
              risk model. earn, reinvest, build, ship, learn what went wrong, repeat.
            </div>
          </div>

          {/* Infra */}
          <div className="jt-panel jt-panel--infra">
            <div className="jt-panel__hdr">
              <span>INFRA · jneural.net</span>
              <span className="jt-dim">live · 1s tick</span>
            </div>
            <ul className="jt-stats">
              <li>
                <span>GPU TEMP</span>
                <strong>{tel.gpuTemp.toFixed(1)}°C</strong>
                <Spark value={tel.gpuTemp} max={90} accent={tel.gpuTemp > 80 ? '#ff6a55' : '#4ad99c'} />
              </li>
              <li>
                <span>CPU LOAD</span>
                <strong>{tel.cpuLoad.toFixed(0)}%</strong>
                <Spark value={tel.cpuLoad} max={100} />
              </li>
              <li>
                <span>RAM USE</span>
                <strong>{tel.ram.toFixed(0)}%</strong>
                <Spark value={tel.ram} max={100} accent="#5b7fff" />
              </li>
              <li>
                <span>NET RX</span>
                <strong>{tel.netRx.toFixed(0)} Mb/s</strong>
                <Spark value={tel.netRx} max={1000} accent="#ffd24a" />
              </li>
              <li>
                <span>NET TX</span>
                <strong>{tel.netTx.toFixed(0)} Mb/s</strong>
                <Spark value={tel.netTx} max={1000} accent="#ffd24a" />
              </li>
              <li>
                <span>UPTIME</span>
                <strong>{Math.floor(tel.uptimeH / 24)}d {Math.floor(tel.uptimeH % 24)}h</strong>
                <Spark value={100} max={100} accent="#4ad99c" />
              </li>
            </ul>
          </div>

          {/* Inference queue */}
          <div className="jt-panel jt-panel--queue">
            <div className="jt-panel__hdr">
              <span>INFERENCE QUEUE</span>
              <span className="jt-dim">linux · docker</span>
            </div>
            <div className="jt-queue">
              <div className="jt-queue__big">
                <strong>{tel.jobs}</strong>
                <span>jobs in flight</span>
              </div>
              <ul className="jt-queue__list">
                <li><code>yolov8n-int8</code> · vision classify · prio 1</li>
                <li><code>whisper-tiny</code> · radio transcript · prio 2</li>
                <li><code>pso-tune</code> · pid optim sweep · prio 3</li>
                <li><code>anomaly-iforest</code> · admin panel · prio 3</li>
              </ul>
            </div>
          </div>

          {/* Containers / pipelines */}
          <div className="jt-panel jt-panel--ops">
            <div className="jt-panel__hdr">
              <span>CONTAINER FLEET</span>
              <span className="jt-dim">docker · {tel.containers} up</span>
            </div>
            <ul className="jt-ops">
              <li className="jt-ops__row jt-ops__row--ok">
                <span className="jt-ops__dot" />
                <span className="jt-ops__name">jneural-site</span>
                <span className="jt-ops__meta">nginx · :8502</span>
                <span className="jt-ops__state">healthy</span>
              </li>
              <li className="jt-ops__row jt-ops__row--ok">
                <span className="jt-ops__dot" />
                <span className="jt-ops__name">terminal-api</span>
                <span className="jt-ops__meta">node · :4001</span>
                <span className="jt-ops__state">healthy</span>
              </li>
              <li className="jt-ops__row jt-ops__row--ok">
                <span className="jt-ops__dot" />
                <span className="jt-ops__name">gitlab-ce</span>
                <span className="jt-ops__meta">internal · :8929</span>
                <span className="jt-ops__state">healthy</span>
              </li>
              <li className="jt-ops__row jt-ops__row--ok">
                <span className="jt-ops__dot" />
                <span className="jt-ops__name">caddy-edge</span>
                <span className="jt-ops__meta">tls · :443</span>
                <span className="jt-ops__state">healthy</span>
              </li>
              <li className="jt-ops__row jt-ops__row--ok">
                <span className="jt-ops__dot" />
                <span className="jt-ops__name">cloudflared</span>
                <span className="jt-ops__meta">tunnel · jneural.net</span>
                <span className="jt-ops__state">healthy</span>
              </li>
              <li className="jt-ops__row jt-ops__row--warn">
                <span className="jt-ops__dot" />
                <span className="jt-ops__name">inference-worker</span>
                <span className="jt-ops__meta">linux · cuda</span>
                <span className="jt-ops__state">heavy</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JneuralTerminal;
